import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Check, Building2, Monitor } from 'lucide-react'
import { useAdScheduleTemplateApplications } from '@/hooks/useAdScheduleTemplateById'
import { useAllClinics } from '@/hooks/useAllClinics'
import { useAllBroadcastDevices } from '@/hooks/useAllBroadcastDevices'
import { useAuth } from '@/hooks/useAuth'
import { adScheduleTemplateService } from '@/services/ad_schedule_template_service'
import { toast } from 'sonner'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { logger } from '@magnetar/utils'

export default function ApplyTemplatePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { token } = useAuth()
  const { template, isLoading, mutate } = useAdScheduleTemplateApplications({
    template_id: id || null,
  })
  const { clinics = [] } = useAllClinics()
  const { devices: broadcastDevices = [] } = useAllBroadcastDevices()

  const [selectedClinicIds, setSelectedClinicIds] = useState<Set<number>>(new Set())
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<number>>(new Set())
  const [isApplying, setIsApplying] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState('clinics')

  const handleToggleClinic = (clinicId: number) => {
    const newSelected = new Set(selectedClinicIds)
    if (newSelected.has(clinicId)) {
      newSelected.delete(clinicId)
    } else {
      newSelected.add(clinicId)
    }
    setSelectedClinicIds(newSelected)
  }

  const handleToggleDevice = (deviceId: number) => {
    const newSelected = new Set(selectedDeviceIds)
    if (newSelected.has(deviceId)) {
      newSelected.delete(deviceId)
    } else {
      newSelected.add(deviceId)
    }
    setSelectedDeviceIds(newSelected)
  }

  const handleApply = async () => {
    if (!token || !id) return
    if (selectedClinicIds.size === 0 && selectedDeviceIds.size === 0) {
      toast.error('請至少選擇一個診所或設備')
      return
    }

    setIsApplying(true)
    try {
      const result = await adScheduleTemplateService.applyTemplate(
        parseInt(id),
        {
          clinic_ids: selectedClinicIds.size > 0 ? Array.from(selectedClinicIds) : undefined,
          broadcast_device_ids:
            selectedDeviceIds.size > 0 ? Array.from(selectedDeviceIds) : undefined,
        },
        token,
      )
      if (result.success) {
        setSelectedClinicIds(new Set())
        setSelectedDeviceIds(new Set())
        mutate()
      }
    } catch (error) {
      logger.error('套用失敗', error)
    } finally {
      setIsApplying(false)
    }
  }

  const handleRemoveApplication = async (applicationId: number) => {
    if (!token) return

    const result = await adScheduleTemplateService.removeApplication(applicationId, token)
    if (result.success) {
      mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">找不到模版</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          返回列表
        </Button>
      </div>
    )
  }

  // 取得已套用的診所和設備 ID
  const appliedClinicIds = new Set(
    template.applications
      ?.filter((app) => app.clinic_id && app.is_active)
      .map((app) => app.clinic_id!),
  )
  const appliedDeviceIds = new Set(
    template.applications
      ?.filter((app) => app.broadcast_device_id && app.is_active)
      .map((app) => app.broadcast_device_id!),
  )

  const searchLower = searchTerm.toLowerCase()
  const filteredClinics = clinics
    .filter((clinic) => {
      const tags = clinic.tags ?? []
      return (
        clinic.clinic_name.toLowerCase().includes(searchLower) ||
        tags.some((t) => t.toLowerCase().includes(searchLower))
      )
    })
    .sort((a, b) => {
      const aApplied = appliedClinicIds.has(a.clinic_id)
      const bApplied = appliedClinicIds.has(b.clinic_id)
      // 未套用的排在前面（false < true）
      if (aApplied === bApplied) return 0
      return aApplied ? 1 : -1
    })

  // 使用從 API 獲取的所有廣播設備
  const allDevices = broadcastDevices.map((device) => ({
    ...device,
    clinic_name: device.clinic?.clinic_name || '未知診所',
    clinic_tags: device.clinic?.tags ?? [],
  }))

  const filteredDevices = allDevices
    .filter(
      (device) =>
        device.name?.toLowerCase().includes(searchLower) ||
        device.clinic_name.toLowerCase().includes(searchLower) ||
        device.clinic_tags.some((t) => t.toLowerCase().includes(searchLower)),
    )
    .sort((a, b) => {
      const aApplied = appliedDeviceIds.has(a.broadcast_device_id)
      const bApplied = appliedDeviceIds.has(b.broadcast_device_id)
      // 未套用的排在前面（false < true）
      if (aApplied === bApplied) return 0
      return aApplied ? 1 : -1
    })

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/ad-schedule-templates/${id}/edit`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">套用廣告排班模版</h1>
          <p className="text-muted-foreground">{template.template_name}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>選擇套用目標</CardTitle>
            <CardDescription>選擇要套用此模版的診所或廣播設備</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="clinics">
                  <Building2 className="mr-2 h-4 w-4" />
                  診所
                </TabsTrigger>
                <TabsTrigger value="devices">
                  <Monitor className="mr-2 h-4 w-4" />
                  廣播設備
                </TabsTrigger>
              </TabsList>

              <div className="mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={activeTab === 'clinics' ? '搜尋診所...' : '搜尋設備...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <TabsContent value="clinics">
                <ScrollArea className="h-[400px] mt-4">
                  <div className="space-y-2">
                    {filteredClinics.map((clinic) => {
                      const isApplied = appliedClinicIds.has(clinic.clinic_id)
                      const clinicTags = clinic.tags ?? []
                      return (
                        <div
                          key={clinic.clinic_id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border ${
                            isApplied ? 'bg-muted/50' : 'hover:bg-muted/50 cursor-pointer'
                          }`}
                          onClick={() => !isApplied && handleToggleClinic(clinic.clinic_id)}
                        >
                          <Checkbox
                            checked={selectedClinicIds.has(clinic.clinic_id) || isApplied}
                            disabled={isApplied}
                            onCheckedChange={() => handleToggleClinic(clinic.clinic_id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{clinic.clinic_name}</p>
                              {clinicTags.map((tag) => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                            <p className="text-sm text-muted-foreground">{clinic.address}</p>
                          </div>
                          {isApplied && (
                            <Badge variant="secondary">
                              <Check className="mr-1 h-3 w-3" />
                              已套用
                            </Badge>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="devices">
                <ScrollArea className="h-[400px] mt-4">
                  <div className="space-y-2">
                    {filteredDevices.map((device) => {
                      const isApplied = appliedDeviceIds.has(device.broadcast_device_id)
                      return (
                        <div
                          key={device.broadcast_device_id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border ${
                            isApplied ? 'bg-muted/50' : 'hover:bg-muted/50 cursor-pointer'
                          }`}
                          onClick={() =>
                            !isApplied && handleToggleDevice(device.broadcast_device_id)
                          }
                        >
                          <Checkbox
                            checked={selectedDeviceIds.has(device.broadcast_device_id) || isApplied}
                            disabled={isApplied}
                            onCheckedChange={() => handleToggleDevice(device.broadcast_device_id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">
                              {device.name || `設備 ${device.broadcast_device_id}`}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm text-muted-foreground">{device.clinic_name}</p>
                              {device.clinic_tags.map((tag) => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          {isApplied && (
                            <Badge variant="secondary">
                              <Check className="mr-1 h-3 w-3" />
                              已套用
                            </Badge>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>

            <div className="mt-4 flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                已選擇 {selectedClinicIds.size + selectedDeviceIds.size} 個目標
              </p>
              <Button
                onClick={handleApply}
                disabled={
                  isApplying || (selectedClinicIds.size === 0 && selectedDeviceIds.size === 0)
                }
              >
                套用模版
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>目前套用狀態</CardTitle>
            <CardDescription>已套用此模版的診所和設備</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-4">
                {template.applications?.filter((app) => app.is_active).length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">尚未套用到任何診所或設備</p>
                ) : (
                  <>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">診所</h4>
                      {template.applications
                        ?.filter((app) => app.clinic && app.is_active)
                        .map((app) => {
                          const clinicTags = app.clinic!.tags ?? []
                          return (
                            <div
                              key={app.application_id}
                              className="flex items-center justify-between p-3 rounded-lg border"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium">{app.clinic!.clinic_name}</p>
                                    {clinicTags.map((tag) => (
                                      <Badge key={tag} variant="secondary" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    套用於 {new Date(app.applied_at).toLocaleDateString('zh-TW')}
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveApplication(app.application_id)}
                              >
                                移除
                              </Button>
                            </div>
                          )
                        })}
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">廣播設備</h4>
                      {template.applications
                        ?.filter((app) => app.broadcast_device && app.is_active)
                        .map((app) => (
                          <div
                            key={app.application_id}
                            className="flex items-center justify-between p-3 rounded-lg border"
                          >
                            <div className="flex items-center gap-3">
                              <Monitor className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="font-medium">
                                  {app.broadcast_device!.name ||
                                    `設備 ${app.broadcast_device!.broadcast_device_id}`}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  套用於 {new Date(app.applied_at).toLocaleDateString('zh-TW')}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveApplication(app.application_id)}
                            >
                              移除
                            </Button>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
